package co.maytes.standupbot.dashboard.features.analytics

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.lib.api.ApiClient
import co.maytes.standupbot.dashboard.lib.api.ApiError
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.shared.dto.analytics.CostsDto
import co.maytes.standupbot.shared.dto.analytics.ParticipationDto
import co.maytes.standupbot.shared.dto.analytics.VelocityDto
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock

class AnalyticsStore(private val api: ApiClient) {
    var costsState: ResourceState<CostsDto> by mutableStateOf(ResourceState.Idle)
        private set
    var velocityState: ResourceState<VelocityDto> by mutableStateOf(ResourceState.Idle)
        private set
    var participationState: ResourceState<ParticipationDto> by mutableStateOf(ResourceState.Idle)
        private set

    private var loadsInFlight: Int by mutableStateOf(0)

    val isRefreshing: Boolean
        get() = loadsInFlight > 0 &&
            costsState is ResourceState.Loaded &&
            velocityState is ResourceState.Loaded &&
            participationState is ResourceState.Loaded

    private val costsCache = mutableMapOf<String, CacheEntry<CostsDto>>()
    private val velocityCache = mutableMapOf<String, CacheEntry<VelocityDto>>()
    private val participationCache = mutableMapOf<String, CacheEntry<ParticipationDto>>()

    suspend fun loadAll(workspaceId: String, range: DateRange) {
        coroutineScope {
            launch { loadCosts(workspaceId, range) }
            launch { loadVelocity(workspaceId, range) }
            launch { loadParticipation(workspaceId, range) }
        }
    }

    suspend fun loadCosts(workspaceId: String, range: DateRange) {
        val key = "$workspaceId:${range.cacheKey}"
        costsCache[key]?.takeIf { it.fresh() }?.let {
            costsState = ResourceState.Loaded(it.value)
            return
        }
        if (costsState !is ResourceState.Loaded) costsState = ResourceState.Loading
        loadsInFlight++
        try {
            val res = api.get<CostsDto>(path = "/api/v1/workspaces/$workspaceId/analytics/costs${range.toQuery()}")
            costsState = res.toResource { costsCache[key] = CacheEntry(it, Clock.System.now().toEpochMilliseconds()) }
        } finally {
            loadsInFlight--
        }
    }

    suspend fun loadVelocity(workspaceId: String, range: DateRange) {
        val key = "$workspaceId:${range.cacheKey}"
        velocityCache[key]?.takeIf { it.fresh() }?.let {
            velocityState = ResourceState.Loaded(it.value)
            return
        }
        if (velocityState !is ResourceState.Loaded) velocityState = ResourceState.Loading
        loadsInFlight++
        try {
            val res = api.get<VelocityDto>(path = "/api/v1/workspaces/$workspaceId/analytics/velocity${range.toQuery()}")
            velocityState = res.toResource { velocityCache[key] = CacheEntry(it, Clock.System.now().toEpochMilliseconds()) }
        } finally {
            loadsInFlight--
        }
    }

    suspend fun loadParticipation(workspaceId: String, range: DateRange) {
        val key = "$workspaceId:${range.cacheKey}"
        participationCache[key]?.takeIf { it.fresh() }?.let {
            participationState = ResourceState.Loaded(it.value)
            return
        }
        if (participationState !is ResourceState.Loaded) participationState = ResourceState.Loading
        loadsInFlight++
        try {
            val res = api.get<ParticipationDto>(path = "/api/v1/workspaces/$workspaceId/analytics/participation${range.toQuery()}")
            participationState = res.toResource { participationCache[key] = CacheEntry(it, Clock.System.now().toEpochMilliseconds()) }
        } finally {
            loadsInFlight--
        }
    }

    private fun <T> Result<T, ApiError>.toResource(onOk: (T) -> Unit): ResourceState<T> = when (this) {
        is Result.Ok -> {
            onOk(value)
            ResourceState.Loaded(value)
        }
        is Result.Err -> ResourceState.Error(error)
    }

    private data class CacheEntry<T>(val value: T, val loadedAtMs: Long) {
        fun fresh(): Boolean = Clock.System.now().toEpochMilliseconds() - loadedAtMs < CACHE_TTL_MS
    }

    companion object {
        private const val CACHE_TTL_MS = 5L * 60L * 1000L
    }
}

sealed interface ResourceState<out T> {
    data object Idle : ResourceState<Nothing>
    data object Loading : ResourceState<Nothing>
    data class Loaded<T>(val value: T) : ResourceState<T>
    data class Error(val error: ApiError) : ResourceState<Nothing>
}

fun ResourceState<*>.isPending(): Boolean = this is ResourceState.Idle || this is ResourceState.Loading

@Composable
fun rememberAnalyticsStore(): AnalyticsStore {
    val api = rememberApiClient()
    return remember(api) { AnalyticsStore(api) }
}
